import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@/api/client';
import {
  useAddProgramItems,
  useCreateBlock,
  useDeleteBlock,
  useUpdateBlock,
  useRemoveProgramItem,
  useReorderProgram,
} from '@/api/queries';
import type { Block, EventCard } from '@/api/types';
import { Button } from '@/components/kit/Button';
import { ChoiceGroup, Option, RowAction, TextLink } from '@/components/kit/Controls';
import { Col, Row, TextField } from '@/components/kit/Field';
import { ActionBar } from '@/components/kit/ActionBar';
import { Kv, KvGrid } from '@/components/kit/Layout';
import { Modal } from '@/components/kit/Modal';
import { Alert, EmptyState, Note } from '@/components/kit/Note';
import { SectionHeader } from '@/components/kit/ObjectTabs';
import { Panel, Signals, Stack } from '@/components/kit/Panel';
import { Pill, type PillTone } from '@/components/kit/Pill';
import { MobileSub, OrderButtons } from '@/components/kit/Table';
import { useToast } from '@/components/kit/Toast';
import {
  date as fmtDate,
  daysOfEvent,
  duration,
  nominations as nomPlural,
  num,
  teams as teamsPlural,
  timeRange,
} from '@/lib/format';
import { SERVICE_KIND } from '../state';

/**
 * Вкладка «Программа».
 *
 * Блок — контейнер программы: номинации, классы и шоукейсы лежат
 * в одном упорядоченном списке. Порядок задаёт режиссёр руками,
 * автоматики нет — потому и две стрелки вместо перетаскивания.
 *
 * Шапки у таблиц позиций нет намеренно: четыре блока подряд,
 * четыре одинаковые шапки — это шум, а колонки очевидны
 * из содержимого. Ширины держит colgroup.
 */
export function ProgramTab({ event }: { event: EventCard }) {
  const toast = useToast();
  const [newBlock, setNewBlock] = useState(false);
  const [editing, setEditing] = useState<Block | null>(null);
  const [addTo, setAddTo] = useState<Block | null>(null);
  const [removing, setRemoving] = useState<Block | null>(null);

  const outOfProgram = event.nominations.filter((n) =>
    event.computed.nominationsOutOfProgram.includes(n.id),
  );

  const addBlock = (
    <Button small tone="quiet" onClick={() => setNewBlock(true)}>
      ＋ Блок
    </Button>
  );

  return (
    <>
      <Signals>
        {outOfProgram.length ? (
          <Alert>
            <b>{nomPlural(outOfProgram.length)} вне программы.</b>
            <span>
              {outOfProgram.map((n) => n.name).join(', ')} — добавьте их в блок, иначе они не попадут
              в порядок выступлений
            </span>
          </Alert>
        ) : null}
      </Signals>

      {/* Раздел — стопка панелей-блоков, поэтому шапка раздела стоит над ними */}
      <SectionHeader actions={addBlock} />

      {event.blocks.length === 0 ? (
        <Panel>
          <EmptyState>
            Блоков пока нет. Блок — часть дня со своим запуском зрителей и своим награждением
          </EmptyState>
        </Panel>
      ) : (
        <Stack>
          {event.blocks.map((block) => (
            <BlockSection
              key={block.id}
              event={event}
              block={block}
              onAdd={() => setAddTo(block)}
              onEdit={() => setEditing(block)}
              onRemove={() => setRemoving(block)}
            />
          ))}
        </Stack>
      )}

      <ActionBar>{addBlock}</ActionBar>

      {newBlock ? <BlockModal event={event} onClose={() => setNewBlock(false)} /> : null}

      {editing ? (
        <BlockModal event={event} block={editing} onClose={() => setEditing(null)} />
      ) : null}

      {addTo ? (
        <AddToProgramModal event={event} block={addTo} onClose={() => setAddTo(null)} />
      ) : null}

      {removing ? (
        <DeleteBlockModal
          event={event}
          block={removing}
          onClose={() => setRemoving(null)}
          onDone={(msg) => {
            setRemoving(null);
            toast.show(msg);
          }}
        />
      ) : null}
    </>
  );
}

function BlockSection({
  event,
  block,
  onAdd,
  onEdit,
  onRemove,
}: {
  event: EventCard;
  block: Block;
  onAdd: () => void;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const reorder = useReorderProgram(event.id);
  const removeItem = useRemoveProgramItem(event.id);

  const ticket = event.services.find((s) => s.kind === 'ticket' && s.blockId === block.id);
  const time = timeRange(block.doorsAt, block.startsAt);

  const teams = block.items.reduce((sum, item) => {
    const n = event.nominations.find((x) => x.id === item.nominationId);
    return sum + (n?.teamLimit ?? 0);
  }, 0);

  const move = (index: number, delta: number) => {
    const ids = block.items.map((i) => i.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorder.mutate(
      { blockId: block.id, itemIds: ids },
      { onError: (e) => toast.showError(e instanceof ApiError ? e.message : 'Не получилось') },
    );
  };

  return (
    <Panel
      title={block.name}
      actions={
        <>
          <Button small tone="quiet" onClick={onAdd}>
            ＋ Добавить
          </Button>
          <Button small tone="quiet" onClick={onEdit}>
            Изменить
          </Button>
          <Button small tone="quiet" onClick={onRemove}>
            Удалить
          </Button>
        </>
      }
    >

      <KvGrid>
        <div>
          <Kv k="Дата">
            <span className="num">{fmtDate(block.date)}</span>
          </Kv>
          <Kv k="Время">
            {time ? (
              <span className="num">
                {block.doorsAt ? `запуск ${block.doorsAt}` : ''}
                {block.doorsAt && block.startsAt ? ' · ' : ''}
                {block.startsAt ? `старт ${block.startsAt}` : ''}
              </span>
            ) : (
              <span className="dim">время не задано</span>
            )}
          </Kv>
        </div>
        <div>
          <Kv k="Билет в блок">
            {ticket ? (
              <TextLink onClick={() => navigate(`/events/${event.id}/services/${ticket.id}`)}>
                {ticket.name}
              </TextLink>
            ) : (
              <span className="dim">не заведён</span>
            )}
          </Kv>
          <Kv k="Тайминг план">
            {block.planSeconds ? (
              <span className="num">{duration(block.planSeconds)}</span>
            ) : (
              <span className="dim">номеров нет</span>
            )}
          </Kv>
        </div>
      </KvGrid>

      {block.items.length === 0 ? (
        <EmptyState>В блоке пусто. Добавьте номинацию, класс или шоукейс</EmptyState>
      ) : (
        <table className="tbl prog">
          <colgroup>
            <col className="pc-n" />
            <col />
            <col className="pc-type" />
            <col className="pc-time" />
            <col className="pc-seats" />
            <col className="pc-act" />
          </colgroup>
          <tbody>
            {block.items.map((item, index) => {
              const info = describe(event, item);
              if (!info) return null;

              return (
                <tr key={item.id}>
                  <td className="num muted" data-col="hide">
                    {index + 1}
                  </td>
                  <td data-col="primary">
                    <span className="nm" onClick={() => navigate(info.to)}>
                      {info.name}
                    </span>
                    {info.sub ? <span className="dim"> {info.sub}</span> : null}
                    <MobileSub>
                      № {index + 1} · {info.type}
                      {info.time ? ` · ${info.time}` : ''}
                    </MobileSub>
                  </td>
                  <td data-col="secondary">
                    <Pill tone={info.tone}>{info.type}</Pill>
                  </td>
                  <td className="r" data-col="sec2">
                    {info.time ? (
                      <span className="num">{info.time}</span>
                    ) : (
                      <span className="na">—</span>
                    )}
                  </td>
                  <td className="r num muted" data-col="hide">
                    {info.seats ?? <span className="na">—</span>}
                  </td>
                  <td className="r" data-col="act">
                    <OrderButtons
                      canUp={index > 0}
                      canDown={index < block.items.length - 1}
                      onUp={() => move(index, -1)}
                      onDown={() => move(index, 1)}
                    />
                    <RowAction
                      title="Убрать из программы"
                      onClick={() =>
                        removeItem.mutate(item.id, {
                          onSuccess: () => toast.show(`«${info.name}» убрана из программы`),
                          onError: (e) =>
                            toast.showError(e instanceof ApiError ? e.message : 'Не получилось'),
                        })
                      }
                    />
                  </td>
                </tr>
              );
            })}

            <tr className="tot">
              <td colSpan={4}>Итого по блоку</td>
              <td className="r num">{teams ? teamsPlural(teams) : <span className="na">—</span>}</td>
              <td className="r" data-col="act" />
            </tr>
          </tbody>
        </table>
      )}
    </Panel>
  );
}

interface ItemInfo {
  name: string;
  sub?: string;
  type: string;
  tone: PillTone;
  time: string | null;
  seats: string | null;
  to: string;
}

function describe(event: EventCard, item: Block['items'][number]): ItemInfo | null {
  if (item.nominationId) {
    const n = event.nominations.find((x) => x.id === item.nominationId);
    if (!n) return null;
    return {
      name: n.name,
      type: n.isCompetitive ? 'Номинация' : 'Показательная',
      tone: n.isCompetitive ? 'mut' : 'info',
      time: null,
      seats: n.teamLimit ? `${num(n.teamLimit)}` : null,
      to: `/events/${event.id}/nominations/${n.id}`,
    };
  }

  const s = event.services.find((x) => x.id === item.serviceId);
  if (!s) return null;

  const kind = SERVICE_KIND[s.kind];
  return {
    name: s.name,
    sub: s.kind === 'showcase' && s.choreographer ? `· ${s.choreographer}` : undefined,
    type: kind.label,
    tone: s.kind === 'masterclass' ? 'warn' : s.kind === 'showcase' ? 'info' : 'mut',
    time: timeRange(s.startsAt, s.endsAt),
    seats: s.capacity ? num(s.capacity) : null,
    to: `/events/${event.id}/services/${s.id}`,
  };
}

// ─── окна ───

/**
 * Создание и правка блока — одно окно.
 *
 * Полей четыре, и различие между «завести» и «поменять» тут сводится
 * к начальным значениям. Два почти одинаковых окна разъехались бы
 * на первой же правке.
 */
function BlockModal({
  event,
  block,
  onClose,
}: {
  event: EventCard;
  /** Пусто — создаём новый */
  block?: Block;
  onClose: () => void;
}) {
  const create = useCreateBlock(event.id);
  const update = useUpdateBlock(event.id);
  const toast = useToast();

  const [name, setName] = useState(block?.name ?? `PART ${event.blocks.length + 1}`);
  const [date, setDate] = useState(block?.date ?? event.dateFrom);
  const [doorsAt, setDoorsAt] = useState(block?.doorsAt ?? '');
  const [startsAt, setStartsAt] = useState(block?.startsAt ?? '');

  const pending = create.isPending || update.isPending;

  const submit = () => {
    const body = {
      name,
      date,
      doorsAt: doorsAt.trim() || null,
      startsAt: startsAt.trim() || null,
    };
    const done = {
      onSuccess: () => {
        toast.show(block ? `Блок «${name}» изменён` : `Блок «${name}» создан`);
        onClose();
      },
      onError: (e: Error) =>
        toast.showError(e instanceof ApiError ? e.message : 'Не сохранилось'),
    };

    if (block) update.mutate({ id: block.id, ...body }, done);
    else create.mutate(body, done);
  };

  return (
    <Modal
      title={block ? `Блок «${block.name}»` : 'Новый блок'}
      onClose={onClose}
      confirm={{
        label: block ? 'Сохранить' : 'Создать',
        onClick: submit,
        disabled: !name.trim() || pending,
      }}
    >
      <TextField label="Название" value={name} onValue={setName} />

      {/*
        Дата — парный выбор, а не календарь.

        Календарь это поповер, а поповер внутри модального окна
        обрезается его `overflow:hidden` (ТЗ §7, «конфликт слоёв»).
        И выбирать тут не из чего: мероприятие идёт от одного
        до трёх дней, блок стоит в один из них.
      */}
      <div className="field">
        <span className="lbl">Дата</span>
        <ChoiceGroup
          value={date}
          onChange={setDate}
          items={daysOfEvent(event.dateFrom, event.dateTo).map((d) => ({
            value: d,
            label: fmtDate(d),
          }))}
        />
      </div>
      <Row>
        <Col fix>
          <TextField
            label="Запуск зрителей"
            placeholder="09:00"
            size={8}
            value={doorsAt}
            onValue={setDoorsAt}
          />
        </Col>
        <Col fix>
          <TextField
            label="Старт"
            placeholder="09:00"
            size={8}
            value={startsAt}
            onValue={setStartsAt}
          />
        </Col>
      </Row>
      <Note>Время можно не задавать: тайминг проясняется позже состава</Note>
    </Modal>
  );
}

/**
 * Добавление в программу.
 *
 * Поповер внутри окна не используется — конфликт слоёв. Здесь список
 * опций с поиском: позиций бывает под тридцать, и без поиска окно
 * превращается в прокрутку.
 */
function AddToProgramModal({
  event,
  block,
  onClose,
}: {
  event: EventCard;
  block: Block;
  onClose: () => void;
}) {
  const add = useAddProgramItems(event.id);
  const toast = useToast();

  const [selected, setSelected] = useState<Record<string, 'nomination' | 'service'>>({});
  const [query, setQuery] = useState('');

  const inProgram = new Set(
    event.blocks.flatMap((b) => b.items.map((i) => i.nominationId ?? i.serviceId)),
  );

  const q = query.trim().toLowerCase();
  const match = (name: string) => !q || name.toLowerCase().includes(q);

  const freeNominations = event.nominations.filter((n) => !inProgram.has(n.id) && match(n.name));
  // Билет привязывается к блоку своим полем, в программу он не кладётся
  const freeServices = event.services.filter(
    (s) => s.kind !== 'ticket' && !inProgram.has(s.id) && match(s.name),
  );

  const count = Object.keys(selected).length;

  const submit = () =>
    add.mutate(
      {
        blockId: block.id,
        items: Object.entries(selected).map(([refId, kind]) => ({ kind, refId })),
      },
      {
        onSuccess: () => {
          toast.show(`Добавлено в «${block.name}»: ${count}`);
          onClose();
        },
        onError: (e) => toast.showError(e instanceof ApiError ? e.message : 'Не добавилось'),
      },
    );

  const toggle = (id: string, kind: 'nomination' | 'service') =>
    setSelected((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = kind;
      return next;
    });

  return (
    <Modal
      title={`Добавить в «${block.name}»`}
      onClose={onClose}
      size="wide"
      confirm={{
        label: count ? `Добавить · ${count}` : 'Добавить',
        onClick: submit,
        disabled: !count || add.isPending,
      }}
    >
      {/* Поле формы, а не поиск ряда фильтров: ширину даёт колонка окна,
          а не style — инлайн-ширины в разметке запрещены */}
      <TextField
        label="Поиск"
        placeholder="по названию"
        value={query}
        onValue={setQuery}
      />

      {!freeNominations.length && !freeServices.length ? (
        <EmptyState>
          Свободных позиций нет — всё уже разложено по блокам
        </EmptyState>
      ) : null}

      {freeNominations.length ? (
        <div className="field">
          <span className="lbl">Номинации</span>
          {freeNominations.map((n) => (
            <Option
              key={n.id}
              checked={Boolean(selected[n.id])}
              onToggle={() => toggle(n.id, 'nomination')}
              title={n.name}
              description={`лимит ${num(n.teamLimit)} · ср. время ${n.avgDurationSec} сек`}
            />
          ))}
        </div>
      ) : null}

      {freeServices.length ? (
        <div className="field">
          <span className="lbl">Классы и шоукейсы</span>
          {freeServices.map((s) => (
            <Option
              key={s.id}
              checked={Boolean(selected[s.id])}
              onToggle={() => toggle(s.id, 'service')}
              title={s.name}
              description={SERVICE_KIND[s.kind].label}
            />
          ))}
        </div>
      ) : null}
    </Modal>
  );
}

function DeleteBlockModal({
  event,
  block,
  onClose,
  onDone,
}: {
  event: EventCard;
  block: Block;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const remove = useDeleteBlock(event.id);
  const toast = useToast();

  const nominationCount = block.items.filter((i) => i.nominationId).length;
  const ticket = event.services.find((s) => s.kind === 'ticket' && s.blockId === block.id);

  const submit = () =>
    remove.mutate(block.id, {
      onSuccess: () => onDone(`Блок «${block.name}» удалён`),
      onError: (e) => toast.showError(e instanceof ApiError ? e.message : 'Не удалось удалить'),
    });

  return (
    <Modal
      title={`Удалить блок «${block.name}»?`}
      onClose={onClose}
      confirm={{ label: 'Удалить', onClick: submit, danger: true, disabled: remove.isPending }}
    >
      <p className="txt">Удаление разрешено всегда, но содержимое блока никуда не денется:</p>
      {nominationCount ? (
        <Note tone="warn">
          <b>{nomPlural(nominationCount)}</b> вернутся в нераспределённые
        </Note>
      ) : null}
      {ticket ? (
        <Note tone="warn">
          Билет <b>{ticket.name}</b> останется без блока — такой билет никуда не пускает
        </Note>
      ) : null}
      {!nominationCount && !ticket ? <Note>Блок пуст, терять нечего</Note> : null}
    </Modal>
  );
}
